<template>
  <SitesBrowserNavigation
    :info="navBarInfo"
    :currentPageId="currentPageId"
    :rsCollection="rsCollection"
    :lastExploreTab="lastExploreTab"
    @changePage="changePage"
  />
  <div class="relative w-full h-full">
    <div class="z-50 absolute top-3 left-3">
      <SitesEditorToolbar
        @buttonClick="handleAction"
        :show-text-visibility-toggle="false"
        :imageKey="currentPage?.imageKey"
        :xmlKey="currentPage?.xmlKey"
        :hideOnSites="currentPage?.hideOnSites"
        ref="toolbar"
      ></SitesEditorToolbar>
    </div>

    <Combined
      :isSites="true"
      :searchParams="searchParams"
      :hideOnSites="currentPage?.hideOnSites"
      ref="combined"
    />
  </div>
</template>

<script>
import Combined from './Views/Combined.vue'
import model from './Model/model.js'
import { Singleton as CollectionConfig } from './LayoutEditor/components/singletons/collectionConfig.js'
import { Singleton as Settings } from '~/components/Editor-single-page/LayoutEditor/components/singletons/settings.js'

export default {
  components: {
    Combined,
  },
  props: {
    rsCollection: {
      type: Object,
      default: () => ({}),
    },
    lastExploreTab: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      config: null,
      pages: [],
      currentPageId: null,
      versions: [],
      currentVersionKey: null,
      navBarInfo: null,
      searchParams: null,
      resultsPageId: null,
      rectCoords: [],
      tagMode: 'showtags',
    }
  },

  setup() {
    const fileStoreApi = useFileStore()

    const sitesApi = useSites()
    return { sitesApi, fileStoreApi }
  },

  created() {
    if (this.$route.query.term == null) return
    this.rectCoords = []
    if (this.$route.query.rectCoords) {
      try {
        const rectCoordsJson = atob(this.$route.query.rectCoords)
        this.rectCoords = JSON.parse(rectCoordsJson)
      } catch (error) {
        console.error('Failed to parse rectCoords:', error)
      }
    }
    this.searchParams = {
      terms: [this.$route.query.term],
      rectCoords: this.rectCoords,
    }
  },

  async mounted() {
    let config
    try {
      config = await this.sitesApi.fetchSitesTags({
        url: this.rsCollection.url,
      })
    } catch (err) {
      console.error(err)
    }
    this.config = config

    if (this.config) {
      CollectionConfig.instance().setConfig(this.config)
    }

    this.resultsPageId = this.getCurrentPageNr()

    await this.fetchDocMeta()
    await this.setPageInfo(this.getCurrentPageNr())
    this.updateEditorPage()
    await this.setVersion(this.currentPage.xmlKey)
    this.updateEditorVersion()

    Settings.instance().set({ showRegions: true }) // showTags == true
  },
  watch: {},
  computed: {
    currentPage() {
      return this.pages.find(p => p.pageId === this.currentPageId)
    },
    currentVersion() {
      return this.versions.find(v => v.xmlKey === this.currentVersionKey)
    },
  },

  methods: {
    async changePage(newPageId) {
      this.currentPageId = null
      this.currentVersionKey = null
      this.updateEditorPage()
      this.updateEditorVersion()

      let { url, docid } = this.$route.params
      window.history.pushState(
        {},
        '',
        `/sites/${url}/doc/${docid}/detail?pageid=${newPageId}`
      )

      await this.setPageInfo(newPageId)
      // reset the rectCoords if the new page is not the results page
      this.searchParams = {
        term: this.$route.query.term,
        rectCoords: this.resultsPageId === newPageId ? this.rectCoords : [],
      }
      this.updateEditorPage()
      await this.setVersion(this.currentPage.xmlKey)
      this.updateEditorVersion()
      this.$refs.toolbar?.applyOverlay()
    },
    handleAction(action) {
      this.$refs.combined.buttonClick(action)

      if (action === 'hidetags') {
        Settings.instance().set({ showRegions: false })
      } else if (action === 'showtags') {
        Settings.instance().set({ showRegions: true })
      }
    },
    updateEditorVersion() {
      this.$refs.combined?.updateEditorVersion(this.currentVersion)
    },
    updateEditorPage() {
      this.$refs.combined?.updateEditorPage(this.currentPage)
    },
    async setVersion(newXmlKey) {
      if (this.currentPage?.hideOnSites) return
      await this.addVersion(newXmlKey)
      this.currentVersionKey = newXmlKey
      model.setPageJson(this.currentVersion.pageJson)
    },
    async addVersion(xmlKey) {
      if (this.versions.find(v => v.xmlKey === xmlKey)) return
      const newVersion = await this.getVersion(xmlKey)
      this.versions.push(newVersion)
    },
    async getVersion(xmlKey) {
      const currentXml = await this.fileStoreApi.fetchPageContentXMLString({
        id: xmlKey,
      })
      const pageJson = model.xmlStringToJson(currentXml)
      return {
        textData: model.getTextData(pageJson),
        layoutData: model.getLayoutData(pageJson),
        pageJson,
        xmlKey,
      }
    },
    async getPageInfo(pageId) {
      const data = await this.sitesApi.fetchRSPageByNum({
        id: this.$route.params.docid,
        pageNum: pageId,
        collection: this.rsCollection?.colId,
        url: this.rsCollection?.url,
      })
      const iiifObject = await $fetch(data.image, 'GET', {})
      return {
        imageDimensions: {
          width: iiifObject.width,
          height: iiifObject.height,
        },
        imageKey: data.pageData.imgKey,
        xmlKey: data.pageData.xmlKey,
        iiifInfo: data.image,
        pageId,
        hideOnSites: data.pageData.hideOnSites,
      }
    },
    async setPageInfo(pageId) {
      await this.addPageInfo(pageId)
      this.currentPageId = pageId
    },
    async addPageInfo(pageId) {
      if (this.pages.find(p => p.pageId === pageId)) return
      const newPageInfo = await this.getPageInfo(pageId)
      this.pages.push(newPageInfo)
    },
    async fetchDocMeta() {
      const meta = await this.sitesApi.fetchRSDocument({
        collection: this.rsCollection?.colId,
        id: this.$route.params.docid,
        url: this.rsCollection?.url,
      })
      this.navBarInfo = {
        maxPage: meta.pageCount,
        title: meta.title,
        docId: meta.id,
      }
    },
    getCurrentPageNr() {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)
      return Number(params.get('pageNr')) || Number(params.get('pageid')) || 1
    },
    updateEditor(xml) {
      const pageJson = model.xmlStringToJson(xml)
      this.$refs.combined?.updateEditorVersion({
        textData: model.getTextData(pageJson),
        layoutData: model.getLayoutData(pageJson),
        xmlKey: xml,
      })
      this.$refs.combined?.updateEditorPage({
        imageDimensions: {
          width: data.width,
          height: data.height,
        },
        imageKey: data.key,
        transcripts: data.tsList.transcripts,
        iiifInfo: `${this.runtimeConfig.public.fileStore}/iiif/2/${data.key}/info.json`,
        pageId,
      })
    },
  },
}
</script>
<style scoped></style>
